import Script from 'next/script';
import getConfig from 'next/config';
import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { fireSnapchatPageView, snapchatInitAndFirePageView } from 'tracking/utils/snapchatEvents';

declare global {
  interface Window {
    snaptr: any;
    isSnapLoaded?: boolean;
  }
}

interface ISnapchatScriptProps {
  cspNonce: string;
  isOneTrustEnabled: boolean;
  clientDedupID: string;
  isUserAuthenticated: boolean;
}

export const SnapchatScript = ({
  cspNonce,
  isOneTrustEnabled,
  clientDedupID,
  isUserAuthenticated,
}: ISnapchatScriptProps) => {
  const {
    publicRuntimeConfig: { SNAPCHAT_PIXEL_ID },
  } = getConfig();
  const { events, query } = useRouter();

  useEffect(() => {
    const handleFireSnapchatPageView = () => {
      if (query?.productTemplateSlug) {
        return;
      }
      fireSnapchatPageView(clientDedupID);
    };

    events.on('routeChangeComplete', handleFireSnapchatPageView);

    return () => {
      events.off('routeChangeComplete', handleFireSnapchatPageView);
    };
  }, [events, clientDedupID]);

  const initialFireSnapchatTracker = (retry = 5, previousTimeout = null) => {
    if (query?.productTemplateSlug) {
      return;
    }

    const { status } = snapchatInitAndFirePageView({
      isUserAuthenticated,
      SNAPCHAT_PIXEL_ID,
    });

    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    if (status !== 'success' && retry > 0) {
      const timeOutID = setTimeout(() => {
        initialFireSnapchatTracker(retry - 1, timeOutID);
      }, 1000);
    }
  };

  return (
    <Script
      key="snapchat"
      nonce={cspNonce}
      strategy="lazyOnload"
      className="optanon-category-C0004"
      type={isOneTrustEnabled ? 'text/plain' : 'text/javascript'}
      id="snapchat-script"
      onError={(error) => bugsnag(error, { context: 'Snapchat Inline Script Erroring' })}
      onReady={initialFireSnapchatTracker}
    >
      {`
        (function(e, t, n) {
          if (e.snaptr) return;
          var a = e.snaptr = function() {
            a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
          };
          a.queue = [];
          var s = 'script';
          r = t.createElement(s);
          r.async = !0;
          r.src = n;
          var u = t.getElementsByTagName(s)[0];
          u.parentNode.insertBefore(r, u);
        })(window, document, 'https://sc-static.net/scevent.min.js');

        snaptr('init', '${SNAPCHAT_PIXEL_ID as string}', { 'user_email': ''});
      `}
    </Script>
  );
};
